import React, { useEffect, useMemo, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useFragment, graphql } from 'react-relay'

import { ImageProps, getTokenValue, Token, styled } from 'tamagui'

import { Image } from '../Image'

import { UserPrimaryPhotoNew_user$key } from './__generated__/UserPrimaryPhotoNew_user.graphql'

const UserPrimaryPhotoFrame = styled(Image, {
  name: 'UserPrimaryPhoto',
  objectFit: 'cover',
  // @ts-expect-error objectPosition is valid CSS
  objectPosition: 'center',
  overflow: 'hidden',
  aspectRatio: 1,
  flexShrink: 0,
  boxShadow: '0 0 0 2px var(--background)',
  // boxShadow: '0 0 0 16px #FF0000',
  borderColor: '$borderColor',
  borderWidth: 1,
  backgroundColor: '$background',

  variants: {
    thinShadow: {
      true: {
        boxShadow: '0 0 0 1px var(--background)',
      },
    },
  },

  defaultVariants: {
    thinShadow: false,
  },
} as const)

const GravatarWithFallback = React.forwardRef<
  HTMLImageElement,
  {
    emailMd5: string
    defaultAvatarUrl: string | null
    alt: string
    parsedSize: number
    size: Token
    [key: string]: unknown
  }
>(({ emailMd5, defaultAvatarUrl, alt, parsedSize, size, ...props }, ref) => {
  // Check if a real Gravatar exists. d=blank returns a small transparent PNG
  // (~287 bytes) when no avatar is set; real Gravatars are well over 1KB.
  // We use a HEAD request to check content-length without downloading the image,
  // avoiding both 404 console noise and the previous bug where the blank PNG
  // replaced the default avatar.
  const gravatarUrl = `https://gravatar.com/avatar/${emailMd5}?d=blank&s=${parsedSize * 3}`
  const [src, setSrc] = useState(defaultAvatarUrl || gravatarUrl)

  useEffect(() => {
    let cancelled = false
    setSrc(defaultAvatarUrl || gravatarUrl)

    fetch(gravatarUrl, { method: 'HEAD', mode: 'cors' })
      .then((res) => {
        const len = Number(res.headers.get('content-length') || '0')
        // Real Gravatars are well over 1KB; d=blank is ~287 bytes
        if (!cancelled && res.ok && len > 1000) {
          setSrc(gravatarUrl)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [defaultAvatarUrl, gravatarUrl])

  return (
    <UserPrimaryPhotoFrame
      {...props}
      alt={alt}
      ref={ref}
      src={src}
      loading='lazy'
      width={parsedSize}
      height={parsedSize}
      borderRadius={size}
    />
  )
})
GravatarWithFallback.displayName = 'GravatarWithFallback'

export const UserPrimaryPhoto = UserPrimaryPhotoFrame.styleable<{
  user: UserPrimaryPhotoNew_user$key
  size: Token
}>(({ user, size = '$5', ...props }, ref) => {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment UserPrimaryPhotoNew_user on User
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        firstName
        lastName
        rowId
        emailMd5

        primaryPhoto {
          transformUrl(
            transform: {
              w: $imageSize
              h: $imageSize
              dpr: $dpr
              fo: "face"
              fit: "cover"
            }
          )
        }

        # Signed URL for default avatar when no custom photo is set
        defaultAvatarUrl(transform: { w: $imageSize, h: $imageSize, dpr: $dpr })
      }
    `,
    user
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size') as number
  }, [size])

  const alt = t`Primary Photo for ${data.firstName} ${data.lastName}`

  if (data.primaryPhoto?.transformUrl) {
    return (
      <UserPrimaryPhotoFrame
        {...props}
        alt={alt}
        ref={ref}
        src={data.primaryPhoto.transformUrl}
        width={parsedSize}
        height={parsedSize}
        borderRadius={size}
      />
    )
  }

  // No primary photo: show default avatar, then async-check for a real Gravatar.
  if (data.emailMd5) {
    return (
      <GravatarWithFallback
        emailMd5={data.emailMd5}
        defaultAvatarUrl={data.defaultAvatarUrl}
        alt={alt}
        parsedSize={parsedSize}
        size={size}
        ref={ref}
        {...props}
      />
    )
  }

  return (
    <UserPrimaryPhotoFrame
      {...(props as any)}
      alt={alt}
      ref={ref}
      src={data.defaultAvatarUrl}
      width={parsedSize}
      height={parsedSize}
      borderRadius={size}
    />
  )
})

export const UserDefaultPhoto = UserPrimaryPhotoFrame.styleable<
  { defaultAvatarUrl: string } & Omit<ImageProps, 'source'>
>(({ defaultAvatarUrl, ...props }, ref) => {
  return (
    <UserPrimaryPhotoFrame
      {...(props as any)}
      ref={ref}
      src={defaultAvatarUrl}
    />
  )
})
